import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import UtilisateurProfileLayout from './UtilisateurProfileLayout';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { ExclamationTriangleFill } from 'react-bootstrap-icons';
import UtilisateurService from '../../services/UtilisateurService';
import RoleService from '../../services/RoleService';
import PersonnelService from '../../services/PersonnelService';

const ViewUtilisateur = () => {
	const { id } = useParams();
	const [utilisateur, setUtilisateur] = useState(null);
	const [roleOptions, setRoleOptions] = useState([]);
	const [personnelOptions, setPersonnelOptions] = useState([]);
	const [validated, setValidated] = useState(false);
	const [feedbackMessages, setFeedbackMessages] = useState({});
	const [showAlert, setShowAlert] = useState(false);
	const [alertMessage, setAlertMessage] = useState('');
	const [selectedPersonnel, setSelectedPersonnel] = useState(null);
	const [selectedRole, setSelectedRole] = useState(null);

	useEffect(() => {
		Promise.all([
			UtilisateurService.getUtilisateur(id),
			RoleService.getAllRoles(),
			PersonnelService.getPersonnelOptions()
		]).then(([utilisateurData, roleData, personnelData]) => {
			setUtilisateur(utilisateurData.data);

			const utilisateurPersonnel = personnelData.find(
				(personnel) => personnel.value === utilisateurData.data.personnel
			);

			setSelectedPersonnel(utilisateurPersonnel.value || '');

			const utilisateurRole = roleData.find(
				(role) => role.id === utilisateurData.data.role
			);
			setSelectedRole({
				value: utilisateurRole?.id || '',
				label: utilisateurRole?.nom.toUpperCase() || ''
			});

			// Format role options for Select component
			const formattedRoleOptions = roleData.map((role) => ({
				value: role.id,
				label: role.nom.toUpperCase()
			}));
			setRoleOptions(formattedRoleOptions);
			setPersonnelOptions(personnelData);
		});
	}, [id]);

	const PERSONNEL_CHOICES = personnelOptions;

	const handleSubmit = async (event) => {
		event.preventDefault();
		const form = event.currentTarget;

		if (form.checkValidity() === false) {
			event.stopPropagation();
		} else {
			try {
				const utilisateurData = {
					personnel: selectedPersonnel.value,
					role: selectedRole.value,
					username: utilisateur.username,
					password: utilisateur.password
				};

				await UtilisateurService.updateUtilisateur(id, utilisateurData);

				// Reset the form and state variables after successful update
				setValidated(false);
				setFeedbackMessages({});
				window.location.reload();
			} catch (error) {
				console.error(
					"Erreur lors de la mise à jour des informations de l'utilisateur",
					error
				);

				if (error.response && error.response.data) {
					const apiErrors = error.response.data;

					// If there are field-specific errors
					if (typeof apiErrors === 'object' && !Array.isArray(apiErrors)) {
						const newFeedbackMessages = {};

						for (const field in apiErrors) {
							newFeedbackMessages[field] = apiErrors[field][0];
						}

						setFeedbackMessages(newFeedbackMessages);
					}

					// If there's a general error detail message
					if (apiErrors.detail) {
						setAlertMessage(apiErrors.detail[0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					} else if (
						apiErrors['Il ya erreur'] &&
						apiErrors['Il ya erreur'][0]
					) {
						setAlertMessage(apiErrors['Il ya erreur'][0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					}
				} else {
					alert(
						"Une erreur s'est produite lors de la soumission du formulaire."
					);
				}
			}
		}

		setValidated(true);
	};

	return (
		<UtilisateurProfileLayout>
			{utilisateur && (
				<Card className="mb-3">
					<Card.Body>
						<Form noValidate validated={validated} onSubmit={handleSubmit}>
							{showAlert && (
								<Alert variant="danger">
									<ExclamationTriangleFill size={25} className="me-1" />
									{alertMessage}
								</Alert>
							)}

							<Row>
								{/* Personnel */}
								<Col xs={12} className="mb-3">
									<Form.Group className="mb-3">
										<Form.Label htmlFor="personnel">
											Employé <span className="text-danger">*</span>
										</Form.Label>
										<Form.Select
											name="personnel"
											required
											disabled
											value={selectedPersonnel}
											isInvalid={!!feedbackMessages['personnel']}
											onChange={(e) => {
												const value = e.target.value;
												setSelectedPersonnel(value);
												// Reset feedback messages for points fields when personnel changes
												setFeedbackMessages({});
											}}
										>
											<option value="">Sélectionner le personnel</option>
											{PERSONNEL_CHOICES.map((choice, index) => (
												<option key={index} value={choice.value}>
													{choice.label}
												</option>
											))}
										</Form.Select>
										<Form.Control.Feedback personnel="invalid">
											{feedbackMessages['personnel']}
										</Form.Control.Feedback>
									</Form.Group>
								</Col>

								{/* Role */}
								<Col xs={12} className="mb-3">
									<Form.Group className="mb-3">
										<Form.Label htmlFor="role">
											Rôle <span className="text-danger">*</span>
										</Form.Label>

										<Form.Select
											name="role"
											required
											value={selectedRole?.value || ''}
											isInvalid={!!feedbackMessages['role']}
											onChange={(e) => {
												const value = parseInt(e.target.value, 10); // Convertir en entier pour correspondre à l'ID
												setFeedbackMessages((prevMessages) => ({
													...prevMessages,
													role: value
														? ''
														: "Le rôle de l'utilisateur est requis."
												}));

												const selected = roleOptions.find(
													(option) => option.value === value
												);

												if (selected) {
													setSelectedRole(selected);
												} else {
													console.error('Role not found for value:', value);
												}
											}}
										>
											<option value="">Sélectionner un rôle</option>
											{roleOptions.map((option, index) => (
												<option key={index} value={option.value}>
													{option.label}
												</option>
											))}
										</Form.Select>

										<Form.Control.Feedback type="invalid">
											{feedbackMessages['role']}
										</Form.Control.Feedback>
									</Form.Group>
								</Col>

								{/* Buttons */}
								<Col xs={12} className="mt-4">
									<Button variant="success" type="submit">
										Mettre à jour
									</Button>
									<Button
										type="reset"
										variant="outline-danger"
										className="ms-2"
									>
										Effacer
									</Button>
								</Col>
							</Row>
						</Form>
					</Card.Body>
				</Card>
			)}
		</UtilisateurProfileLayout>
	);
};

export default ViewUtilisateur;
